import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './lib/appContext';
import { api, loginUrl, type BatchState } from './api';

function Header() {
  const { user, config, refreshUser } = useApp();

  // Only the required login role/group is shown next to the name.
  const roleLabel = config?.auth.testerGroup ?? 'tester';

  return (
    <header className="app-header">
      <Link to="/" className="brand">
        <span className="brand-dot" /> AI Performance Evaluator
      </Link>
      <nav>
        <NavLink to="/" end>
          Overview
        </NavLink>
        <NavLink to="/run">Tests</NavLink>
      </nav>
      <div className="header-auth">
        {user ? (
          <>
            <span className="user-chip">
              {user.name ?? user.email ?? user.sub}
              {user.isTester ? (
                <span className="badge ok" title="Login role granted">
                  {roleLabel}
                </span>
              ) : (
                <span className="badge" title={`Requires "${roleLabel}"`}>
                  {roleLabel}: no
                </span>
              )}
            </span>
            <button
              className="btn ghost"
              onClick={async () => {
                await api.logout();
                await refreshUser();
              }}
            >
              Logout
            </button>
          </>
        ) : (
          <a className="btn" href={loginUrl}>
            Login
          </a>
        )}
      </div>
    </header>
  );
}

function LoginBanner() {
  const { config } = useApp();
  const params = new URLSearchParams(window.location.search);
  const login = params.get('login');
  if (login === 'denied') {
    const group = config?.auth.testerGroup;
    return (
      <div className="banner error">
        Sign-in denied
        {group ? <> – membership in the group “{group}” is required.</> : '.'}
      </div>
    );
  }
  if (login === 'error') {
    const reason = params.get('reason');
    return (
      <div className="banner error">
        Sign-in failed{reason ? ` (${reason})` : ''}. See the server log for details.
      </div>
    );
  }
  return null;
}

// Polls the server for a benchmark run in progress and shows a persistent strip
// on every page (the worker keeps running regardless of navigation).
function RunTicker() {
  const location = useLocation();
  const [batch, setBatch] = useState<BatchState | null>(null);

  useEffect(() => {
    let alive = true;
    const tick = () =>
      api
        .currentBatch()
        .then((r) => alive && setBatch(r.batch))
        .catch(() => undefined);
    tick();
    const iv = window.setInterval(tick, 5000);
    return () => {
      alive = false;
      window.clearInterval(iv);
    };
  }, []);

  if (!batch || batch.status !== 'running' || location.pathname === '/run') return null;

  const model = batch.models[batch.currentIndex]?.model ?? '';
  const done = batch.models.filter((m) => m.status === 'done').length;
  return (
    <Link to="/run" className="run-ticker">
      <span className="run-ticker-dot" />
      Benchmark run in progress – {model} (model {Math.min(batch.currentIndex + 1, batch.models.length)}/
      {batch.models.length}, {done} done) · open →
    </Link>
  );
}

function Shell() {
  const { loading, error, config } = useApp();
  return (
    <div className="app-shell">
      <Header />
      <RunTicker />
      <main className="app-main">
        <LoginBanner />
        {error ? (
          <div className="banner error">Failed to load: {error}</div>
        ) : loading ? (
          <div className="loading">Loading…</div>
        ) : (
          <Outlet />
        )}
      </main>
      <footer className="app-footer">
        {config?.hardware ? <span className="footer-hw">Hardware: {config.hardware}</span> : null}
        Results are public. Running tests requires SSO sign-in and the configured group.
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
